import EventEmitter from '../common/EventEmitter';
import { InternalEvents, ProxyIngestData } from '../events';
import {
  SourceType,
  TransferRecord,
  Node,
  Item
} from '../webrtc/agent/interfaces';
import { HttpResponse, HttpResponseBody, HttpRequest } from './interfaces';
import { PlayType } from '../common/constants';
import { isManifestUrl } from '../common/utils/sourceTypeUtils';
import { itemsContainUrl } from './itemsContainUrl';

function getSizeBytes(body?: HttpResponseBody): number {
  if (!body) {
    return 0;
  }
  if (body && (body as ArrayBuffer).byteLength) {
    return (body as ArrayBuffer).byteLength;
  } else if (body && (body as string).length) {
    return (body as string).length;
  } else {
    return 0;
  }
}

type IngestData = {
  data?: HttpResponse;
  cacheOnly?: boolean;
  error?: { request: HttpRequest; error: Error };
};

const isManifest = (items: Item[], url: string) => {
  return itemsContainUrl(items, url) || isManifestUrl(url);
};

export interface GetOptsResult {
  items: Item[];
  node: Node;
  appName: string;
  peerEnabled: boolean;
  playType: PlayType;
  cacheHandler: (url: string, data: { payload: ArrayBuffer }) => void;
}

const makeTransfer = ({
  node,
  response,
  appName,
  playType,
  peerEnabled,
  sourceType,
  sourceId
}: {
  node: Node;
  response: HttpResponse;
  appName: string;
  playType: PlayType;
  peerEnabled: boolean;
  sourceType: SourceType;
  sourceId: string;
}): TransferRecord => {
  const { request } = response;
  const sizeBytes = getSizeBytes(response.body);
  const {
    nodeId,
    contentId,
    tenantId,
    clusterId,
    externalIp,
    internalIp,
    internalMdns
  } = node;
  const requestEnd = response.timer.getEnd();
  const loadInit = requestEnd - 1;

  const data: TransferRecord = {
    type: sourceType,
    sourceId,
    nodeId,
    contentId,
    objectId: request.url,
    component: { id: 'payload', pos: 0, sizeBytes },
    timestamp: Date.now(),
    requestStart: request.timer.getStart(),
    loadInit,
    requestEnd,
    status: response.status >= 200 && response.status < 400,
    statusCode: response.status,
    tenantId,
    clusterId,
    externalIp,
    internalIp,
    internalMdns,
    appName,
    playType,
    peerEnabled
  };

  return data;
};

const handleEvent = ({
  eventEmitter,
  opts,
  response,
  sourceType,
  cacheOnly,
  sourceId
}: {
  eventEmitter: EventEmitter;
  opts: GetOptsResult;
  response: HttpResponse;
  sourceType: SourceType;
  cacheOnly: boolean;
  sourceId: string;
}) => {
  const { request } = response;
  const { items, node, appName, peerEnabled, playType, cacheHandler } = opts;
  // cache the payload if its an arraybuffer
  if (response.body && (response.body as ArrayBuffer).byteLength) {
    cacheHandler(request.url, { payload: response.body as ArrayBuffer });
  }
  // don't send transfers for manifests when peering or when event says not to send transfer
  if ((peerEnabled && isManifest(items, request.url)) || cacheOnly) {
    return;
  }

  const data = makeTransfer({
    node,
    response,
    appName,
    playType,
    sourceType,
    peerEnabled,
    sourceId
  });
  eventEmitter.emit(InternalEvents.TRANSFER_RECORD, { data });
};

export default function listenToRequestIngest(
  eventEmitter: EventEmitter,
  getOpts: () => GetOptsResult
): void {
  eventEmitter.addEventListener(
    InternalEvents.REQUEST_INGEST,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: Record<string, any>): void => {
      const response = (event as IngestData).data;
      if (!response) {
        return;
      }
      const opts = getOpts();
      handleEvent({
        eventEmitter,
        opts,
        cacheOnly: event.cacheOnly,
        response,
        sourceType: SourceType.INGEST,
        sourceId: response.request.url
      });
    }
  );

  eventEmitter.addEventListener(
    InternalEvents.PROXY_INGEST,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: Record<string, any>): void => {
      if (!event.data) {
        return;
      }
      const {
        data: response,
        sourceType,
        proxyType
      } = event as ProxyIngestData;
      const opts = getOpts();
      handleEvent({
        eventEmitter,
        opts,
        cacheOnly: false,
        response,
        sourceType,
        sourceId: proxyType
      });
    }
  );
}
